import { Add as AddIcon } from '@mui/icons-material';
import { Alert, Box, Button, Checkbox, Grid, Stack } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { isEmpty } from 'lodash';
import SortableList, { SortableItem } from 'react-easy-sort';
import { useScheduleLocationOverridesContext } from 'src/components/context';
import { FullGcfComponentSection } from 'src/components/gcfComponentSection';
import { SimpleDialog } from 'src/components/modal';
import { ScaleContainer } from 'src/components/scaleContainer/ScaleContainer';
import { CustomTooltip } from 'src/components/tooltip/CustomTooltip';
import { DateRangeWidget } from 'src/components/widget';
import { BackupComponent } from 'src/features/assemblies/editAssemblyPage/modules/sectionDetailsSidebar/components/componentInfo/BackupComponent/BackupComponent';
import { getInvalidBackups } from 'src/features/locations/scheduleComponentPage/ScheduleComponentPage.helper';
import { formatDateRange } from 'src/features/locations/scheduleLocationGroupPage/modules/scheduleComponentDrawer/ComponentScheduleDrawer.helper';
import { useModal } from 'src/hooks';
import {
  ComponentTypes,
  DynamicSectionEvents,
  IComponent,
} from 'src/interfaces';
import { lang, useUserRole } from 'src/utils';
import { getSectionPreviewData } from 'src/utils/gcf-helper';

import {
  IScheduledSectionBackupComponent,
  IScheduledSectionComponent,
} from '../../../ScheduleComponentPage.interface';
import {
  getComponentValue,
  getDateDisabledChecker,
  getDateValue,
  isAllowedComponentType,
  validateScheduleComponentDate,
} from '../ComponentAccordion.helper';
import {
  AccordionContentWrapper,
  BackupsWrapper,
  BackupTitle,
  CheckBoxHeader,
  CheckBoxWrapper,
  ComponentSectionWrapper,
  EndDateLabelWrapper,
  ScheduleDateBox,
  ScheduleHeader,
  SortableItemContainer,
  StyledInfoOutlinedIcon,
} from '../ComponentAccordion.styled';
import {
  getOverrideDateLimits,
  isScheduledComponentExpired,
} from './ComponentAccordionContent.helper';

export interface IComponentAccordionContentProps {
  selectedComponent: IScheduledSectionComponent;
  isCodConfirmationOpen: boolean | undefined;
  disabledStartDates: string[];
  options?: {
    enableBackup?: boolean;
    isOverriding?: boolean;
    enabledSwapButton?: boolean;
  };
  onClickRemoveButton: () => void;
  onClickSwapButton: () => void;
  onCLickAddBackupButton: () => void;
  onClickBackupRemoveButton: (
    backupComponent: IScheduledSectionBackupComponent,
  ) => void;
  onBackupSortEnd: (oldIndex: number, newIndex: number) => void;
  onClickBackupName: (
    backupComponent: IScheduledSectionBackupComponent,
  ) => void;
  handleUpdateComponent: (updatedComponent: IScheduledSectionComponent) => void;
  onClickEditProduct?: (component: IScheduledSectionComponent) => void;
}

const EndDateLabelWithTooltip = () => {
  return (
    <EndDateLabelWrapper display="flex" justifyContent="space-between">
      <Box>{lang.endDateOptional}</Box>
      <CustomTooltip
        width={500}
        placement="right"
        skipValidateOverflow
        title={lang.scheduleComponent.scheduleEndDateTooltip}>
        <StyledInfoOutlinedIcon />
      </CustomTooltip>
    </EndDateLabelWrapper>
  );
};

export const ComponentAccordionContent = (
  props: IComponentAccordionContentProps,
) => {
  const {
    selectedComponent,
    isCodConfirmationOpen,
    disabledStartDates,
    options,
    onClickRemoveButton,
    onClickSwapButton,
    onCLickAddBackupButton,
    onClickBackupRemoveButton,
    onBackupSortEnd,
    onClickBackupName,
    handleUpdateComponent,
    onClickEditProduct,
  } = props;
  const {
    enableBackup = true,
    isOverriding,
    enabledSwapButton = true,
  } = options ?? {};

  const { scheduleItemOverrides } = useScheduleLocationOverridesContext();

  const currentOverride = scheduleItemOverrides?.find(
    (o) => o.schedule_item_id === selectedComponent.id,
  );

  const isProductOverride =
    !!currentOverride?.schedule_item_override_variations?.length;

  const removeComponentModal = useModal();
  const { isViewOnly, isOwnerOperator } = useUserRole();

  const handleRemoveComponent = () => {
    onClickRemoveButton();
    removeComponentModal.onCloseModal();
  };

  const handleCLickAddBackupButton = () => {
    onCLickAddBackupButton();
  };

  const onSortEnd = (oldIndex: number, newIndex: number) => {
    onBackupSortEnd(oldIndex, newIndex);
  };

  const allowedProductOverideableComponentTypes = [
    ComponentTypes.LISTING_AND_VISUALS,
    ComponentTypes.LISTING_WITH_OPTIONAL_VISUALS,
    ComponentTypes.LISTING_VISUALS_GALLERY,
    ComponentTypes.SINGLE_SHELF,
  ];

  const handleDateRangeChange = (values: any) => {
    const updatedStartDate = values[0] ? values[0].format('YYYY-MM-DD') : null;
    const updatedEndDate = values[1] ? values[1].format('YYYY-MM-DD') : null;

    if (!selectedComponent.component) return;

    const updatedComponent: IScheduledSectionComponent = {
      ...selectedComponent,
      scheduleStartDate: updatedStartDate,
      scheduleEndDate: updatedEndDate,
      component: {
        ...(selectedComponent.component as IComponent),
        config: {
          ...selectedComponent.component.config,
          ...selectedComponent.override?.component_config,
          productList: {
            ...selectedComponent.component.config?.productList,
            ...selectedComponent.override?.component_config?.productList,
            groups: {
              ...selectedComponent.component.config.productList?.groups,
              ...selectedComponent.override?.component_config?.productList
                ?.groups,
            },
          },
        },
      },
    };

    handleUpdateComponent(updatedComponent);
  };

  const handleToggleAllowOverrides = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const isChecked = e.target.checked;

    const updatedComponent: IScheduledSectionComponent = {
      ...selectedComponent,
      allow_overrides: isChecked,
    };

    handleUpdateComponent(updatedComponent);
  };

  const sectionPreviewData = getSectionPreviewData(
    selectedComponent.component?.component_type?.name as ComponentTypes,
    isCodConfirmationOpen
      ? DynamicSectionEvents.COD_CONFIRMATION_OPEN
      : DynamicSectionEvents.COD_ORDER_OPEN,
  );

  const isBackupsMoreThanFive = (selectedComponent?.backups ?? []).length >= 5;

  const dateRange: [Dayjs | null, Dayjs | null] = [
    getDateValue(
      selectedComponent?.override,
      'start_date',
      selectedComponent?.scheduleStartDate,
    ),
    getDateValue(
      selectedComponent?.override,
      'end_date',
      selectedComponent?.scheduleEndDate,
    ),
  ];

  const showDateChange = !isOverriding || !!selectedComponent?.isOverridden;

  const showRemoveComponentButton =
    !isOverriding || !!selectedComponent?.isOverridden;

  const assemblyComponent = {
    component: getComponentValue(
      selectedComponent?.component,
      selectedComponent?.override?.component,
    ),
  };

  const showOverrideCheckbox =
    !isOverriding && isAllowedComponentType(selectedComponent);

  const invalidBackupIds = getInvalidBackups(
    selectedComponent.scheduleEndDate,
    selectedComponent.backups,
  );

  const shouldShowReplacePromotion =
    isOverriding &&
    (!isOwnerOperator ||
      (isOwnerOperator &&
        selectedComponent.component?.component_type?.name ===
          ComponentTypes.PROMOTIONAL_SPACE));

  const shouldShowSwapComponent = !isOverriding;

  const isReplaceAndSwapButtonDisabled =
    isOwnerOperator || isViewOnly ? !enabledSwapButton : false;

  return (
    <AccordionContentWrapper>
      <ComponentSectionWrapper
        $isConfirmation={
          selectedComponent.component?.component_type?.name ===
            ComponentTypes.COD && !!isCodConfirmationOpen
        }>
        {selectedComponent.component?.config && (
          <ScaleContainer
            containerStyle={{
              height: '100%',
              minWidth: '100%',
            }}>
            <FullGcfComponentSection
              assemblyComponents={[assemblyComponent]}
              height={selectedComponent.component?.height || 0}
              width={selectedComponent.component?.width || 0}
              previewData={sectionPreviewData}
            />
          </ScaleContainer>
        )}
      </ComponentSectionWrapper>
      <Stack direction="row" spacing={2} alignItems="center">
        {showRemoveComponentButton && (
          <Button
            variant="outlined"
            onClick={removeComponentModal.onOpenModal}
            fullWidth>
            {options?.isOverriding ? lang.removeOverride : lang.removeComponent}
          </Button>
        )}

        {shouldShowReplacePromotion && (
          <Button
            disabled={isReplaceAndSwapButtonDisabled}
            variant="outlined"
            onClick={onClickSwapButton}
            fullWidth>
            {lang.replacePromotion}
          </Button>
        )}

        {shouldShowSwapComponent && (
          <Button
            disabled={isReplaceAndSwapButtonDisabled}
            variant="outlined"
            onClick={onClickSwapButton}
            fullWidth>
            {lang.scheduleComponent.swapComponent}
          </Button>
        )}

        {isOwnerOperator &&
          allowedProductOverideableComponentTypes.includes(
            selectedComponent.component?.component_type?.name as ComponentTypes,
          ) && (
            <Button
              disabled={!selectedComponent.allow_overrides}
              variant="outlined"
              data-testid={lang.overrideProducts}
              onClick={() => {
                if (!selectedComponent.component || !onClickEditProduct) return;

                // Merge override config if it exists
                const componentToEdit = currentOverride
                  ? {
                      ...selectedComponent,
                      component: {
                        ...selectedComponent.component,
                        config: currentOverride.component_config,
                      },
                    }
                  : selectedComponent;

                onClickEditProduct(componentToEdit);
              }}
              fullWidth>
              {lang.overrideProducts}
            </Button>
          )}
      </Stack>

      {showDateChange && (
        <ScheduleDateBox>
          <ScheduleHeader>
            {options?.isOverriding ? lang.overrideDates : lang.scheduleDates}
          </ScheduleHeader>
          <DateRangeWidget
            labels={[
              lang.startDate,
              isOverriding ? lang.endDate : <EndDateLabelWithTooltip />,
            ]}
            values={dateRange}
            disabled={false}
            error={
              dateRange && (dateRange[0] || dateRange[1])
                ? validateScheduleComponentDate(
                    dateRange,
                    disabledStartDates,
                    isOverriding,
                    selectedComponent?.scheduleStartDate,
                    selectedComponent?.scheduleEndDate,
                    selectedComponent?.component,
                  )
                : false
            }
            onChange={handleDateRangeChange}
            options={{
              ...formatDateRange(
                undefined,
                selectedComponent?.scheduleStartDate
                  ? dayjs(selectedComponent.scheduleStartDate)
                  : null,
                selectedComponent?.scheduleEndDate
                  ? dayjs(selectedComponent.scheduleEndDate)
                  : null,
              ),
              isStartDateDisabled: (date: Dayjs) =>
                getDateDisabledChecker(
                  'start',
                  selectedComponent.component as IComponent,
                  disabledStartDates,
                )(date),
              isEndDateDisabled: (date: Dayjs) =>
                getDateDisabledChecker(
                  'end',
                  selectedComponent.component as IComponent,
                )(date),
              ...getOverrideDateLimits(
                selectedComponent,
                (isProductOverride
                  ? selectedComponent
                  : selectedComponent.override?.component) as IComponent,
              ),
              showDivider: false,
            }}
          />

          {showOverrideCheckbox && (
            <Grid item xs={12}>
              <CheckBoxHeader>
                {lang.ownerOperator.ownerOperatorHeader}
              </CheckBoxHeader>
              <CheckBoxWrapper component="span">
                <Checkbox
                  data-testid="owner-override-checkbox"
                  checked={selectedComponent.allow_overrides ?? false}
                  onChange={handleToggleAllowOverrides}
                />
                <CheckBoxHeader>
                  {
                    lang.ownerOperator
                      .allowOwnerOperatorSheduleComponentSubheader
                  }
                </CheckBoxHeader>
              </CheckBoxWrapper>
            </Grid>
          )}
        </ScheduleDateBox>
      )}

      {selectedComponent.component?.component_type?.name ===
        ComponentTypes.PROMOTIONAL_SPACE && (
        <BackupsWrapper>
          {!isEmpty(selectedComponent.backups) && (
            <>
              <BackupTitle fontWeight={400} variant="body1">
                {lang.backup}
              </BackupTitle>
              <Alert severity="info">{lang.defaultDurationBackup}</Alert>

              {enableBackup ? (
                <SortableList
                  onSortEnd={onSortEnd}
                  className="list"
                  draggedItemClassName="dragged"
                  lockAxis="y">
                  {selectedComponent.backups?.map((backupComponent) => (
                    <SortableItem key={backupComponent.uid}>
                      {/* Do not remove <div>, otherwise SortableList won't work */}
                      <SortableItemContainer>
                        {invalidBackupIds.includes(backupComponent.uid) && (
                          <Alert severity="error">
                            {lang.scheduleComponent.invalidBackupErrorMsg}
                          </Alert>
                        )}
                        <BackupComponent
                          key={backupComponent.uid}
                          backupComponent={backupComponent}
                          onDeletedBackupAssemblyComponent={
                            onClickBackupRemoveButton
                          }
                          onBackComponentSelection={onClickBackupName}
                          showSoftSellInfo={false}
                          type="ScheduleComponentFlow"
                        />
                      </SortableItemContainer>
                    </SortableItem>
                  ))}
                </SortableList>
              ) : (
                <div className="list">
                  {selectedComponent.backups?.map((backupComponent) => (
                    <div key={backupComponent.uid}>
                      <BackupComponent
                        editable={false}
                        key={backupComponent.uid}
                        backupComponent={backupComponent}
                        onDeletedBackupAssemblyComponent={
                          onClickBackupRemoveButton
                        }
                        onBackComponentSelection={onClickBackupName}
                        showSoftSellInfo={false}
                        type="ScheduleComponentFlow"
                      />
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {enableBackup && (
            <CustomTooltip
              title={isBackupsMoreThanFive ? lang.backupWarningTips : ''}
              placement="right"
              arrow
              isSecondaryTooltip={true}
              fontSize={14}
              skipValidateOverflow={true}>
              {/* Do not remove <div>, otherwise tooltip won't work */}
              <div>
                <Button
                  fullWidth
                  variant="outlined"
                  disabled={
                    isBackupsMoreThanFive ||
                    isScheduledComponentExpired(
                      selectedComponent.scheduleEndDate,
                    )
                  }
                  onClick={handleCLickAddBackupButton}
                  startIcon={<AddIcon />}>
                  {lang.addBackup}
                </Button>
              </div>
            </CustomTooltip>
          )}
        </BackupsWrapper>
      )}

      {removeComponentModal.open && (
        <SimpleDialog
          actionColor="error"
          open={removeComponentModal.open}
          cancelText={lang.cancel}
          confirmText={isOwnerOperator ? lang.remove : lang.deleteText}
          title={
            isOwnerOperator
              ? lang.removeOverride
              : lang.scheduleComponent.removeComponentTitle
          }
          onClose={removeComponentModal.onCloseModal}
          onClickCancel={removeComponentModal.onCloseModal}
          modalSize="sm"
          onClickConfirm={handleRemoveComponent}
          adjustTitlePadding={true}
        />
      )}
    </AccordionContentWrapper>
  );
};
